import { motion } from 'framer-motion';

interface FormProgressHeaderProps {
  currentStep: number;
  totalSteps: number;
  completionPercentage: number;
  onClearForm: () => void;
}

const stepLabels = [
  { short: "Student", long: "Student Information" },
  { short: "Parents", long: "Parent/Guardian Details" },
  { short: "Classroom", long: "Classroom Request" },
  { short: "Final", long: "Final Questions" }
];

export default function FormProgressHeader({ 
  currentStep, 
  totalSteps, 
  completionPercentage, 
  onClearForm 
}: FormProgressHeaderProps) {
  return (
    <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
      <div className="flex justify-between items-center mb-6">
        <div>
          <span className="text-lg font-semibold text-dblue">
            Step {currentStep} of {totalSteps}
          </span>
          <p className="text-sm text-gray-600 mt-1">
            {stepLabels[currentStep - 1]?.long}
          </p>
        </div>
        <div className="text-right flex flex-col items-end gap-2">
          <div>
            <span className="text-2xl font-bold text-dblue">
              {Math.round(completionPercentage)}%
            </span>
            <p className="text-sm text-gray-600">Completed</p>
          </div>
          <button
            onClick={onClearForm}
            className="text-xs text-red-600 hover:text-red-700 hover:underline transition-colors"
            title="Clear all saved form data"
          >
            Clear Form
          </button>
        </div>
      </div>
      
      {/* Enhanced Progress Bar with Steps */}
      <div className="relative mb-8">
        {/* Background bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
          <motion.div 
            className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full shadow-sm"
            initial={{ width: 0 }}
            animate={{ width: `${completionPercentage}%` }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
          />
        </div>
        
        {/* Step indicators */}
        <div className="flex justify-between absolute top-0 w-full transform -translate-y-1">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
            <div key={step} className="flex flex-col items-center">
              <motion.div
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold shadow-sm ${
                  step < currentStep
                    ? 'bg-green-500 border-green-500 text-white'
                    : step === currentStep
                    ? 'bg-blue-500 border-blue-500 text-white'
                    : 'bg-white border-gray-300 text-gray-400'
                }`}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ 
                  scale: step === currentStep ? 1.3 : 1,
                  opacity: 1,
                  rotate: step < currentStep ? 360 : 0
                }}
                transition={{ 
                  duration: 0.5,
                  type: "spring",
                  stiffness: 200,
                  damping: 15
                }}
                key={`${step}-${currentStep}`}
              >
                {step < currentStep ? (
                  <motion.svg 
                    className="w-3 h-3 text-white" 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  >
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </motion.svg>
                ) : (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                  >
                    {step}
                  </motion.span>
                )}
              </motion.div>
              <motion.span 
                className={`text-xs mt-3 font-medium transition-colors duration-300 ${
                  step <= currentStep ? 'text-dblue' : 'text-gray-400'
                }`}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              >
                {stepLabels[step - 1]?.short}
              </motion.span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

